import clsx from 'clsx';
import type { ReactNode } from 'react';

export function Card({
  title,
  subtitle,
  actions,
  children,
  className,
  bodyClassName,
  noPadding = false,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  bodyClassName?: string;
  noPadding?: boolean;
}) {
  return (
    <section
      className={clsx(
        'min-w-0 rounded-xl border border-zinc-800 bg-zinc-900/50 shadow-sm shadow-black/20',
        className,
      )}
    >
      {title || actions ? (
        <header className="flex flex-col gap-2 border-b border-zinc-800 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4">
          <div className="min-w-0">
            {title ? <h2 className="truncate text-sm font-semibold text-zinc-100">{title}</h2> : null}
            {subtitle ? <p className="mt-0.5 text-xs text-zinc-500">{subtitle}</p> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      <div className={clsx(noPadding ? '' : 'p-3 sm:p-4', bodyClassName)}>{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold text-zinc-50 sm:text-xl">{title}</h1>
        {description ? <p className="mt-1 text-xs text-zinc-500 sm:text-sm">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function KeyValue({
  items,
  className,
  columns = 2,
}: {
  items: Array<{ label: ReactNode; value: ReactNode }>;
  className?: string;
  columns?: 1 | 2 | 3;
}) {
  return (
    <dl
      className={clsx(
        'grid gap-x-4 gap-y-2',
        columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3',
        className,
      )}
    >
      {items.map((item, index) => (
        <div key={index} className="min-w-0 border-b border-zinc-800/60 pb-1.5 last:border-0">
          <dt className="text-[11px] uppercase tracking-wide text-zinc-500">{item.label}</dt>
          <dd className="tabular mt-0.5 break-words text-sm text-zinc-100">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
